import type React from 'react';
import { forwardRef, memo, useEffect, useMemo, useRef } from 'react';
import { offset } from '@floating-ui/core';
import {
  useFloating,
  useInteractions,
  useClick,
  useDismiss,
  useHover,
  arrow as arrowMiddleware,
  FloatingArrow,
  autoUpdate,
  useFocus,
  hide,
} from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { useTheme } from 'styled-components';

import { useControlledState } from '@/js/libs/hooks/useControlledState/index.ts';
import { useMergeRefs } from '@/js/libs/hooks/useMergeRefs/index.ts';

import { getFloatingPlacement } from './getFloatingPlacement.tsx';
import { Content, Trigger, Wrapper } from './styled.ts';
import type { Placement, TooltipType, TriggerType } from './types.ts';
import { useExtendedClick } from './useClickExtended.ts';

type ContainerComponentType = React.ElementType;

export type TooltipVisibleChangeHandler = (state: boolean) => void;

export interface TooltipProps {
  children?: React.ReactNode;
  className?: string;
  tooltip?: React.ReactNode;
  /** Component that wraps floating element of tooltip. Default <Wrapper /> */
  WrapperComponent?: React.ComponentType;
  /** Do not use default WrapperComponent. Default: false */
  renderTooltipWithoutWrapper?: boolean;
  disabled?: boolean;
  visible?: boolean;
  closeOnTriggerHidden?: boolean;
  dataTestId?: string;
  /** Component that wraps Tooltip children. Default <Trigger /> */
  containerComponentProps?: React.ComponentProps<ContainerComponentType>;
  trigger?: TriggerType | TriggerType[] | null;
  zIndex?: number;
  placement?: Placement;
  interactive?: true;
  onVisibleChange?: TooltipVisibleChangeHandler;
  ContainerComponent?: ContainerComponentType;
  type?: TooltipType;
  /** Whether to auto-size the tooltip content (max-height: 200px by default) */
  autoSize?: boolean;
  delayShow?: number;
  delayHide?: number;
  arrow?: boolean;
  contentPadding?: boolean;
  /** Override tooltip background color (useful for poppers). */
  backgroundColor?: string;
}

export const Tooltip = memo(
  forwardRef<HTMLElement, TooltipProps>(function Tooltip(
    {
      children,
      className,
      tooltip,
      renderTooltipWithoutWrapper = false,
      WrapperComponent = Wrapper,
      disabled,
      visible,
      closeOnTriggerHidden,
      dataTestId,
      containerComponentProps,
      trigger = 'hover',
      zIndex,
      placement = 'auto',
      onVisibleChange,
      ContainerComponent = Trigger,
      type = 'tooltip',
      autoSize = false,
      contentPadding = true,
      delayShow = 100,
      delayHide = 100,
      arrow = true,
      backgroundColor,
    },
    ref,
  ) {
    const [isVisible, setIsVisible] = useControlledState(visible, false, onVisibleChange);

    const triggers = useMemo(() => {
      let triggersList = Array.isArray(trigger) ? trigger : [trigger];
      if (typeof window !== 'undefined') {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (isTouchDevice) {
          // Not all touch devices can't do right click, so replace it with click
          triggersList = triggersList.reduce((acc, t) => {
            acc.push(t === 'right-click' ? 'click' : t);
            return acc;
          }, new Array<TriggerType | null>());
        }
      }
      return triggersList;
    }, [trigger]);

    const theme = useTheme();
    const [defaultPlacement, placementMiddleware] = getFloatingPlacement(placement);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context, middlewareData } = useFloating({
      placement: defaultPlacement,
      open: isVisible,
      onOpenChange: setIsVisible,
      whileElementsMounted: autoUpdate,
      middleware: [
        offset(5),
        placementMiddleware,
        // eslint-disable-next-line react-hooks/refs
        arrowMiddleware({
          element: arrowRef,
          padding: theme.borders.radiusMedium,
        }),
        closeOnTriggerHidden && hide(),
      ],
    });

    const containerRef = useMergeRefs(refs.setReference, ref);

    // This property added by hide middleware.
    // https://floating-ui.com/docs/hide#referencehidden
    const refHidden = middlewareData.hide?.referenceHidden;
    useEffect(() => {
      if (refHidden) {
        setIsVisible(false);
      }
    }, [refHidden, setIsVisible]);

    const leftClick = useClick(context, {
      event: 'click',
      enabled: triggers.includes('click'),
    });

    const rightClick = useExtendedClick(context, {
      event: 'right-click',
      enabled: triggers.includes('right-click'),
    });

    const hover = useHover(context, {
      delay: {
        open: delayShow,
        close: delayHide,
      },
      enabled: triggers.includes('hover'),
    });

    const focus = useFocus(context, {
      enabled: triggers.includes('focus'),
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([leftClick, hover, rightClick, focus]);

    useDismiss(context, {
      enabled: isVisible,
    });

    return disabled || !tooltip ? (
      <ContainerComponent className={className} ref={ref} data-testid={dataTestId} {...containerComponentProps}>
        {children}
      </ContainerComponent>
    ) : (
      <>
        {isVisible &&
          ReactDOM.createPortal(
            <WrapperComponent
              $zIndex={zIndex}
              ref={refs.setFloating}
              {...getFloatingProps()}
              style={{
                ...floatingStyles,
                ...(backgroundColor ? { background: backgroundColor } : {}),
              }}
              type={type}
              data-testid='tooltip-wrapper'>
              {renderTooltipWithoutWrapper ? (
                tooltip
              ) : (
                <Content $contentPadding={contentPadding} $autoSize={autoSize}>
                  {tooltip}
                </Content>
              )}
              {arrow && (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  fill={backgroundColor ? backgroundColor : type === 'tooltip' ? theme.colors.black_1 : theme.colors.grey00_1}
                  width={11}
                  height={5}
                />
              )}
            </WrapperComponent>,
            document.body,
          )}
        <ContainerComponent
          data-testid={dataTestId}
          {...containerComponentProps}
          className={className}
          ref={containerRef}
          {...getReferenceProps()}>
          {children}
        </ContainerComponent>
      </>
    );
  }),
);

// https://www.figma.com/file/OPrQYe21YjiU2kmoGd571W/AdConsole---Design-System?node-id=648%3A2778
export const DelayedTooltip: React.FC<TooltipProps> = (props) => <Tooltip {...props} delayShow={300} delayHide={500} />;
